import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "./api";
import { loadBanner } from "./pdf";
import { rp, fmtDate } from "./format";
import { fmtCoord } from "./geoPhoto";

const docImage = async (docId) => {
  try {
    const r = await api.get(`/loans/docs/${docId}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    const c = document.createElement("canvas");
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    return { data: c.toDataURL("image/jpeg", 0.85), ratio: img.naturalWidth / img.naturalHeight };
  } catch {
    return null;
  }
};

const fit = (img, maxW, maxH) => { const w = Math.min(maxW, maxH * img.ratio); return [w, w / img.ratio]; };
const table = (doc, y, title, rows) => {
  autoTable(doc, { startY: y, head: [[{ content: title, colSpan: 2 }]], body: rows, theme: "grid",
    headStyles: { fillColor: [11, 58, 158], fontSize: 10 }, styles: { fontSize: 9, cellPadding: 1.8 },
    columnStyles: { 0: { cellWidth: 55, fontStyle: "bold" } }, margin: { left: 14, right: 14 } });
  return doc.lastAutoTable.finalY + 5;
};

export async function downloadLoanLetter(loan) {
  const [{ data: m }, banner] = await Promise.all([api.get(`/admin/loans/${loan.loan_id}/member`), loadBanner()]);
  const byType = Object.fromEntries((loan.documents || []).map((d) => [d.doc_type, d]));
  const doc = new jsPDF();
  if (banner) doc.addImage(banner.data, "PNG", 14, 6, 20 * banner.ratio, 20);
  doc.setDrawColor(220, 38, 38); doc.setLineWidth(0.8); doc.line(14, 29, 196, 29);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(11, 20, 55);
  doc.text("SURAT PERMOHONAN PINJAMAN", 105, 39, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
  doc.text(`No. Pengajuan: ${loan.loan_id}   •   Tanggal: ${fmtDate(loan.created_at)}`, 105, 45, { align: "center" });
  doc.setTextColor(15, 23, 42);
  let y = table(doc, 51, "Data Pemohon", [["Nama", m.name || loan.member_name], ["No. Anggota", m.member_no || loan.member_no], ["NIK", m.nik || "-"],
    ["No. HP", m.phone || "-"], ["Email", m.email || "-"], ["Alamat", m.address || "-"]]);
  y = table(doc, y, "Data Pinjaman", [["Jumlah Pinjaman", rp(loan.amount)], ["Tenor", `${loan.tenor_months} bulan`], ["Bunga", `${loan.rate}% / bulan (flat)`],
    ["Angsuran per Bulan", rp(loan.monthly_installment)], ["Total Pembayaran", rp(loan.total_payable)], ["Tujuan", loan.purpose],
    ["Metode Pencairan", ({ tunai: "Tunai di Kantor CU", sukarela: "Simpanan Sukarela", bank: `Transfer Bank: ${loan.bank_name} ${loan.bank_account} a.n. ${loan.bank_holder}` })[loan.disburse_method] || "-"]]);
  const dsr = loan.income_monthly ? (loan.monthly_installment / loan.income_monthly) * 100 : null;
  y = table(doc, y, "Data Penghasilan", [["Penghasilan per Bulan", rp(loan.income_monthly || 0)], ["Sumber Penghasilan", loan.income_source || "-"],
    ["Tempat Bekerja / Usaha", loan.workplace || "-"], ["Keterangan Pekerjaan", loan.job_desc || "-"], ["Lama Bekerja/Usaha", loan.work_length || "-"],
    ["Rasio Angsuran", dsr == null ? "-" : `${dsr.toFixed(1).replace(".", ",")}%${dsr > 30 ? " (melebihi batas 30%)" : ""}`]]);
  const sv = loan.survey || {};
  if (sv.schedule || sv.report) {
    y = table(doc, y, "Hasil Survei Lapangan", [
      ["Jadwal Survei", sv.schedule ? new Date(sv.schedule.scheduled_at).toLocaleString("id-ID", { dateStyle: "full", timeStyle: "short" }) : "-"],
      ["Petugas Survei", sv.schedule?.officer || sv.report?.by || "-"],
      ["Hasil", sv.report ? ({ layak: "Layak", perlu_pertimbangan: "Perlu Pertimbangan", tidak_layak: "Tidak Layak" })[sv.report.result] : "Belum ada laporan"],
      ["Catatan Petugas", sv.report?.note || "-"],
      ["Dilaporkan", sv.report ? `${sv.report.by} (${sv.report.by_position}) • ${fmtDate(sv.report.at)}` : "-"]]);
  }
  doc.setFontSize(9);
  const stmt = doc.splitTextToSize("Saya menyatakan bahwa data yang saya berikan adalah benar, lengkap, dan dapat dipertanggungjawabkan. Saya bersedia mengikuti ketentuan dan keputusan pengurus CU Tuah Talino.", 182);
  if (y > 230) { doc.addPage(); y = 20; }
  doc.text(stmt, 14, y); y += stmt.length * 4.5 + 4;
  doc.text(`Kalimantan Barat, ${fmtDate(loan.agreed_at || loan.created_at)}`, 140, y); doc.text("Pemohon,", 140, y + 5);
  const sig = byType.ttd && await docImage(byType.ttd.doc_id);
  if (sig) { const [w, h] = fit(sig, 50, 22); doc.addImage(sig.data, "JPEG", 140, y + 7, w, h); }
  doc.setFont("helvetica", "bold"); doc.text(m.name || loan.member_name, 140, y + 34); doc.setFont("helvetica", "normal");
  doc.line(140, y + 35, 190, y + 35);

  const photos = [...["selfie", "rumah", "ktp"].filter((t) => byType[t] && byType[t].mime?.startsWith("image/")).map((t) => byType[t]),
    ...(loan.documents || []).filter((d) => d.doc_type === "survei")];
  if (photos.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.text("Lampiran Foto", 14, 18); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    let py = 24;
    for (const pd of photos) {
      const img = await docImage(pd.doc_id);
      if (!img) continue;
      const [w, h] = fit(img, 120, 70);
      if (py + h + 16 > 290) { doc.addPage(); py = 18; }
      doc.setFont("helvetica", "bold"); doc.text(pd.label, 14, py); doc.setFont("helvetica", "normal");
      doc.addImage(img.data, "JPEG", 14, py + 2, w, h);
      const g = pd.geo;
      if (g) {
        doc.text([`Koordinat: ${fmtCoord(g)}${g.accuracy ? ` (±${g.accuracy} m)` : ""}`, `Waktu: ${new Date(g.taken_at).toLocaleString("id-ID")}`], 14 + w + 4, py + 8);
        doc.textWithLink("Buka di Google Maps", 14 + w + 4, py + 18, { url: `https://www.google.com/maps/search/?api=1&query=${g.lat},${g.lng}` });
      }
      py += h + 12;
    }
  }
  doc.save(`Surat-Pengajuan-${(m.name || loan.member_name).replace(/\s+/g, "-")}-${loan.loan_id}.pdf`);
}
